/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { trackEvent } from '@web-stories-wp/tracking';
import { DropDown } from '@web-stories-wp/design-system';
import { useAPI } from '@web-stories-wp/story-editor/src/app/api';

/**
 * Internal dependencies
 */
import { PaneBottom } from './components';
import { TagsDropdown } from './tagsDropdown';
import useMedia from '../../../../../app/media/useMedia';
import { PaneInner, SearchInputContainer, StyledPane } from '../common/styles';
import { SearchInput } from '../../../common';
import { PROVIDERS } from '../../../../../app/media/media3p/providerConfiguration';
import { ContentType } from '../../../../../app/media/media3p/constants';
import {
  useMediaProviders3pNames,
  ProviderType,
} from '../../../../../app/media';
import { focusStyle } from '../../../../panels/shared';
import styled from 'styled-components';

import paneId from './paneId';
import ProviderTabList from './providerTabList';
import ProviderPanel from './providerPanel';
import { parseSearchTextWithoutType } from './utils/parseSearchTextWithoutType';
import useMediaProvidersFeatures from '@src/web-stories-wp/story-editor/src/app/media/useMediaProvidersFeatures';
import useFolderNavigation from './useFolderNavigation';

const ScrollableContent = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const DEFAULT_MIMIR_CONTENT_TYPE = 'image,video';
const MIMIR_CONTENT_TYPES = [
  {
    label: __('All Assets', 'web-stories'),
    value: DEFAULT_MIMIR_CONTENT_TYPE,
  },
  {
    label: __('Images', 'web-stories'),
    value: ContentType.IMAGE,
  },
  {
    label: __('Videos', 'web-stories'),
    value: ContentType.VIDEO,
  },
];

/**
 * Pane that contains the media 3P integrations.
 *
 * @param {Object} props Component props
 * @return {*} The media pane element for 3P integrations.
 */
function Media3pPane(props) {
  const localSearchTermRef = useRef('');
  const [tags, setTags] = useState({ label: '', group: [] });
  const { isActive, isTabListEnabled, onBreadcrumbChange } = props;

  // Ref for the scrollable container - needed for pagination calculations in PaginatedMediaGallery
  // This is necessary because MediaGalleryContainer has overflow:visible, so the actual
  // scrolling happens in this parent container
  const scrollContainerRef = useRef();

  const {
    folder,
    setInactive,
    player,
    searchTerm,
    selectedProvider,
    setSelectedProvider,
    setSearchTerm,
    tag,
    media3p,
    resetMedia,
  } = useMedia(
    ({
      media3p: {
        state: { folder, selectedProvider, player, searchTerm, tag },
        actions: {
          setInactive,
          setSelectedProvider,
          setSearchTerm,
          resetMedia,
        },
      },
      media3p,
    }) => ({
      folder,
      player,
      setInactive,
      searchTerm,
      selectedProvider,
      setSelectedProvider,
      setSearchTerm,
      tag,
      media3p,
      resetMedia,
    })
  );

  const [selectedMediaType, setSelectedMediaType] = useState(
    DEFAULT_MIMIR_CONTENT_TYPE
  );

  const providers = useMediaProviders3pNames();

  const {
    actions: { getMediaProviderCollections },
  } = useAPI();

  const mediaFeatures = useMediaProvidersFeatures(
    isActive && selectedProvider?.code
  );

  const providerConfiguration =
    PROVIDERS[selectedProvider?.providerConfigurationKey];
  const usesFolderNavigation = providerConfiguration?.usesFolderAsQueryParam;
  const supportsSearch = providerConfiguration?.supportsSearch !== false;

  useEffect(() => {
    if (isActive && !selectedProvider && providers.length > 0) {
      setSelectedProvider(providers[0].provider);
    }
  }, [isActive, selectedProvider, setSelectedProvider, providers]);

  const selectedCategoryId =
    media3p[selectedProvider?.providerConfigurationKey]?.state?.categories
      ?.selectedCategoryId;
  useEffect(() => {
    trackEvent('search', {
      search_type: 'media3p',
      search_term: searchTerm,
      search_filter: selectedProvider?.providerConfigurationKey,
      search_category: selectedCategoryId,
    });
  }, [selectedProvider, searchTerm, selectedCategoryId]);

  const onSearch = useCallback(
    (value) => {
      const trimText = value.trim();
      if (trimText !== searchTerm || player || folder || tag) {
        const isSpecificMimirTypeSelected =
          selectedProvider?.providerConfigurationKey === ProviderType.MIMIR &&
          selectedMediaType !== DEFAULT_MIMIR_CONTENT_TYPE;
        const basePayload = {
          folder: usesFolderNavigation ? folder : '',
          player: '',
          tag: '',
        };
        // add the selected media type in case of Mimir to the search term
        if (isSpecificMimirTypeSelected) {
          setSearchTerm({
            ...basePayload,
            searchTerm: `${trimText} type:${selectedMediaType}`,
          });
        } else {
          setSearchTerm({
            ...basePayload,
            searchTerm: trimText,
          });
        }
      }
    },
    [
      folder,
      player,
      tag,
      searchTerm,
      setSearchTerm,
      selectedMediaType,
      selectedProvider?.providerConfigurationKey,
      usesFolderNavigation,
    ]
  );

  const handleInitialValue = useCallback(() => {
    const searchTextWithoutType = parseSearchTextWithoutType(searchTerm);
    if (searchTerm === searchTextWithoutType) {
      return;
    }
    setSearchTerm({
      searchTerm: searchTextWithoutType,
      folder: usesFolderNavigation ? folder : '',
      player: '',
      tag: '',
    });
    return searchTextWithoutType;
  }, [folder, searchTerm, setSearchTerm, usesFolderNavigation]);

  const handleMediaTypeSelection = useCallback(
    (_event, value) => {
      if (value !== selectedMediaType) {
        setSelectedMediaType(value);

        const searchTextWithoutType = parseSearchTextWithoutType(searchTerm);
        if (value !== DEFAULT_MIMIR_CONTENT_TYPE) {
          setSearchTerm({
            searchTerm: `${searchTextWithoutType} type:${value}`,
            folder: usesFolderNavigation ? folder : '',
            player: '',
            tag: '',
          });
        } else {
          setSearchTerm({
            searchTerm: searchTextWithoutType,
            folder: usesFolderNavigation ? folder : '',
            player: '',
            tag: '',
          });
        }
      }
    },
    [
      folder,
      searchTerm,
      setSearchTerm,
      setSelectedMediaType,
      selectedMediaType,
      usesFolderNavigation,
    ]
  );

  const paneBottomRef = useRef();
  const searchRef = useRef();

  useEffect(() => {
    if (!searchTerm) {
      searchRef.current?.setValue('');
    }
  }, [searchTerm]);

  // Clear breadcrumb when switching away from folder navigation provider
  useEffect(() => {
    if (!usesFolderNavigation && onBreadcrumbChange) {
      onBreadcrumbChange(null);
    }
  }, [usesFolderNavigation, onBreadcrumbChange]);

  useEffect(() => {
    return () => {
      setInactive();
    };
  }, [setInactive]);

  // Fetch tags for providers without folder navigation
  useEffect(() => {
    if (
      mediaFeatures?.supportsSearchByTags &&
      selectedProvider?.mediaProviderId &&
      !usesFolderNavigation
    ) {
      getMediaProviderCollections({
        mediaProviderId: selectedProvider.mediaProviderId,
        searchTerm: '',
      }).then(({ tags: newTags }) => {
        if (localSearchTermRef.current === '') {
          setTags({
            label: 'Most Used Tags',
            group: newTags,
          });
        }
      });
    }
  }, [
    mediaFeatures?.supportsSearchByTags,
    selectedProvider?.mediaProviderId,
    usesFolderNavigation,
    getMediaProviderCollections,
  ]);

  // Use folder navigation for providers with usesFolderAsQueryParam
  const { renderFolderList, shouldShowAssets } = useFolderNavigation({
    selectedProvider: usesFolderNavigation ? selectedProvider : null,
    setSearchTerm,
    setTags,
    requiresFolderSelection:
      providerConfiguration?.requiresFolderSelection ?? false,
    onBreadcrumbChange,
    resetMedia,
    currentFolder: folder,
  });

  const canSearchByTags =
    mediaFeatures?.supportsSearchByTags && selectedProvider?.mediaProviderId;

  const sharedInputProps = {
    disabled: Boolean(
      selectedProvider?.providerConfigurationKey &&
        PROVIDERS[selectedProvider?.providerConfigurationKey]
          .supportsCategories &&
        media3p[selectedProvider?.providerConfigurationKey]?.state.categories
          .selectedCategoryId
    ),
    initialValue: handleInitialValue,
  };

  // TODO(#2368): handle pagination / infinite scrolling
  return (
    <>
      <StyledPane id={paneId} {...props}>
        <PaneInner>
          {/* Search input - always visible */}
          {supportsSearch && (
            <SearchInputContainer>
              {canSearchByTags && !usesFolderNavigation ? (
                <TagsDropdown
                  {...sharedInputProps}
                  inputRef={searchRef}
                  onChange={onSearch}
                  onSearch={(localSearchTerm) => {
                    getMediaProviderCollections({
                      mediaProviderId: selectedProvider.mediaProviderId,
                      searchTerm: localSearchTerm,
                    }).then(
                      ({
                        tags: newTags,
                        tagsSearchTerm: newTagsSearchTerm,
                      }) => {
                        if (
                          newTagsSearchTerm === localSearchTermRef.current ||
                          localSearchTermRef.current.startsWith(
                            newTagsSearchTerm
                          )
                        ) {
                          setTags({
                            label: newTagsSearchTerm ? '' : 'Most Used Tags',
                            group: newTags,
                          });
                        }
                      }
                    );
                  }}
                  tags={tags}
                  setLocalSearchTerm={(newValue) => {
                    localSearchTermRef.current = newValue;
                  }}
                  setTags={setTags}
                />
              ) : (
                <SearchInput
                  {...sharedInputProps}
                  incremental
                  placeholder={__('Search', 'web-stories')}
                  onSearch={onSearch}
                  ref={searchRef}
                />
              )}
            </SearchInputContainer>
          )}
          {selectedProvider?.providerConfigurationKey ===
            ProviderType.MIMIR && (
            <SearchInputContainer>
              <DropDown
                ariaLabel={__('Media type', 'web-stories')}
                options={MIMIR_CONTENT_TYPES}
                selectedValue={selectedMediaType}
                onMenuItemClick={handleMediaTypeSelection}
                selectButtonStylesOverride={focusStyle}
              />
            </SearchInputContainer>
          )}
          {isTabListEnabled && <ProviderTabList providers={providers} />}

          {/* Shared scrollable container for folders and media gallery */}
          <ScrollableContent ref={scrollContainerRef}>
            {/* Folder navigation below search for providers with usesFolderAsQueryParam */}
            {usesFolderNavigation &&
              renderFolderList &&
              !searchTerm &&
              renderFolderList()}

            {/* Asset gallery - hide during folder loading for folder-based providers */}
            {(!usesFolderNavigation || shouldShowAssets) && (
              <PaneBottom ref={paneBottomRef}>
                {providers.map((providerInstance) => {
                  const { code, mediaProviderId, key } = providerInstance;
                  const isActive =
                    code === selectedProvider?.providerConfigurationKey &&
                    (mediaProviderId
                      ? mediaProviderId === selectedProvider?.mediaProviderId
                      : !selectedProvider?.mediaProviderId);
                  return (
                    <ProviderPanel
                      key={key}
                      providerType={code}
                      isActive={isActive}
                      player={player}
                      searchTerm={searchTerm}
                      folder={folder}
                      tag={tag}
                      role="tabpanel"
                      aria-labelledby={`provider-tab-${key}`}
                      id={`provider-tabpanel-${key}`}
                      layoutType={mediaFeatures?.layoutType}
                      enableDeletion={mediaFeatures?.enableDeletion}
                      enableEditInAzzuu={mediaFeatures?.enableEditInAzzuu}
                      scrollContainerRef={scrollContainerRef}
                    />
                  );
                })}
              </PaneBottom>
            )}
          </ScrollableContent>
        </PaneInner>
      </StyledPane>
    </>
  );
}

Media3pPane.propTypes = {
  isActive: PropTypes.bool,
  isTabListEnabled: PropTypes.bool,
  onBreadcrumbChange: PropTypes.func,
};

export default Media3pPane;
